import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const badgeVariants = cva(
  "relative inline-flex h-6 max-w-full items-center gap-1.5 break-words rounded-md px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        outline: "border-border text-foreground",
        success: "border-transparent bg-success/10 text-success hover:bg-success/20",
        warning: "border-transparent bg-warning/10 text-warning hover:bg-warning/20",
        error: "border-transparent bg-destructive/10 text-destructive hover:bg-destructive/20",
        info: "border-transparent bg-info/10 text-info hover:bg-info/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Show a pulsing "live" indicator dot
   * @default false
   */
  live?: boolean;
  /**
   * Animate when count/value changes (useful for numeric badges)
   * Pass a unique key that changes when the value changes
   */
  countKey?: string | number;
}

function Badge({ className, variant, live = false, countKey, children, ...props }: BadgeProps) {
  const prefersReducedMotion = useReducedMotion();
  const showLiveIndicator = live && !prefersReducedMotion;

  return (
    <div
      className={cn(
        badgeVariants({ variant }),
        className
      )}
      {...props}
    >
      {showLiveIndicator && (
        <span
          className="absolute -right-1 -top-1 size-2 animate-pulse rounded-full bg-destructive"
          aria-label="Live"
        />
      )}
      {countKey !== undefined ? (
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={countKey}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 20,
            }}
          >
            {children}
          </motion.span>
        </AnimatePresence>
      ) : (
        children
      )}
    </div>
  );
}

export { Badge, badgeVariants };
